import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { fetchStoringenDashboard, fetchStoringen } from "./Reducers/actions";
import { message } from "../../Reducers/loading/actions";

const FacilityManagementDashboard = ({ 
  fetchStoringenDashboard, 
  fetchStoringen,
  storingenDashboard,
  storingen 
}) => {
  const [loaded, setLoaded] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({});

  useEffect(() => {
    const loadDashboard = async () => {
      setLoaded(false);
      try {
        const stats = await fetchStoringenDashboard();
        if (stats) {
          setDashboardStats(stats);
        }
        await fetchStoringen({ limit: 10, sortBy: 'createdAt', sortOrder: 'desc' });
        setLoaded(true);
      } catch (error) {
        console.error('Error loading facility dashboard:', error);
        setLoaded(true);
      }
    };

    loadDashboard();
  }, []);

  const getPriorityColor = (prioriteit) => {
    switch (prioriteit) {
      case 'kritiek': return 'danger';     // $danger: #C94C4C
      case 'hoog': return 'warning';       // $warning: #E1B339
      case 'normaal': return 'info';       // $info: #639A67
      case 'laag': return 'success';       // $success: #6BA368
      default: return 'secondary';         // $secondary: #D96A29
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'gemeld': return 'warning';     // $warning: #E1B339
      case 'toegewezen': return 'info';    // $info: #639A67
      case 'in_progress': return 'primary'; // $primary: #B1BF41
      case 'voltooid': return 'success';   // $success: #6BA368
      case 'geannuleerd': return 'secondary'; // $secondary: #D96A29
      default: return 'secondary';
    }
  };

  if (!loaded) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Laden...</span>
            </div>
            <h6 className="text-muted">Dashboard wordt geladen...</h6>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {/* Dashboard Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="fw-bold py-3 mb-2">
                <span className="text-muted fw-light">Facility Management /</span> Dashboard
              </h4>
            </div>
            <div>
              <Link to="/facility/storingen/nieuw" className="btn btn-primary">
                <i className="bx bx-plus me-2"></i>Nieuwe Storing
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-lg-3 col-md-6 col-12 mb-4">
          <div className="card">
            <div className="card-body">
              <div className="avatar flex-shrink-0 mb-3">
                <i className="bx bx-wrench bx-sm text-primary"></i>
              </div>
              <span className="fw-semibold d-block mb-1">Totaal Storingen</span>
              <h3 className="card-title mb-2">{dashboardStats.totaalStoringen || 0}</h3>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-12 mb-4">
          <div className="card">
            <div className="card-body">
              <div className="avatar flex-shrink-0 mb-3">
                <i className="bx bx-error-circle bx-sm text-warning"></i>
              </div>
              <span className="fw-semibold d-block mb-1">Open Storingen</span>
              <h3 className="card-title text-warning mb-2">{dashboardStats.openStoringen || 0}</h3>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-12 mb-4">
          <div className="card">
            <div className="card-body">
              <div className="avatar flex-shrink-0 mb-3">
                <i className="bx bx-time bx-sm text-danger"></i>
              </div>
              <span className="fw-semibold d-block mb-1">SLA Overschreden</span>
              <h3 className="card-title text-danger mb-2">{dashboardStats.overschredenSLA || 0}</h3>
            </div>
          </div>
        </div>

        <div className="col-lg-3 col-md-6 col-12 mb-4">
          <div className="card">
            <div className="card-body">
              <div className="avatar flex-shrink-0 mb-3">
                <i className="bx bx-calendar bx-sm text-success"></i>
              </div>
              <span className="fw-semibold d-block mb-1">Vandaag Gemeld</span>
              <h3 className="card-title text-success mb-2">{dashboardStats.storingenVandaag || 0}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Snelle Acties</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <Link to="/facility/storingen" className="btn btn-outline-primary w-100 d-flex align-items-center">
                    <i className="bx bx-list-ul me-2"></i>
                    Alle Storingen
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <Link to="/facility/werkorders" className="btn btn-outline-secondary w-100 d-flex align-items-center">
                    <i className="bx bx-task me-2"></i>
                    Werkorders
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <Link to="/facility/accommodaties" className="btn btn-outline-info w-100 d-flex align-items-center">
                    <i className="bx bx-home me-2"></i>
                    Accommodaties
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <Link to="/facility/categories" className="btn btn-outline-warning w-100 d-flex align-items-center">
                    <i className="bx bx-category-alt me-2"></i>
                    Categorieën
                  </Link>
                </div>
                <div className="col-lg-3 col-md-6 col-12 mb-3">
                  <Link to="/facility/rapportages" className="btn btn-outline-success w-100 d-flex align-items-center">
                    <i className="bx bx-bar-chart me-2"></i>
                    Rapportages
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Storingen */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recente Storingen</h5>
              <Link to="/facility/storingen" className="btn btn-sm btn-outline-primary">
                Alle storingen bekijken
              </Link>
            </div>
            <div className="card-body">
              {storingen && storingen.length > 0 ? (
                <div className="table-responsive text-nowrap">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Storingsnummer</th>
                        <th>Titel</th>
                        <th>Prioriteit</th>
                        <th>Status</th>
                        <th>Accommodatie</th>
                        <th>Aangemaakt</th>
                        <th>Acties</th>
                      </tr>
                    </thead>
                    <tbody className="table-border-bottom-0">
                      {storingen.slice(0, 10).map(storing => (
                        <tr key={storing._id}>
                          <td>
                            <Link to={`/facility/storingen/${storing._id}`} className="fw-bold text-primary">
                              {storing.storingsNummer}
                            </Link>
                          </td>
                          <td>
                            <span className="fw-medium">{storing.titel}</span>
                          </td>
                          <td>
                            <span className={`badge bg-label-${getPriorityColor(storing.prioriteit)}`}>
                              {storing.prioriteit}
                            </span>
                          </td>
                          <td>
                            <span className={`badge bg-label-${getStatusColor(storing.status)}`}>
                              {storing.status}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">
                              {storing.accommodatie ? (
                                <>
                                  <span className="badge bg-label-info me-1">Acc</span>
                                  {storing.accommodatie.naam}
                                </>
                              ) : storing.kampeerplaats ? (
                                <>
                                  <span className="badge bg-label-success me-1">KP</span>
                                  {storing.kampeerplaats.naam}
                                </>
                              ) : (
                                'Geen'
                              )}
                            </span>
                          </td>
                          <td>
                            <span className="text-muted">{moment(storing.createdAt).format('DD-MM-YYYY HH:mm')}</span>
                          </td>
                          <td>
                            <div className="dropdown">
                              <button type="button" className="btn p-0 dropdown-toggle hide-arrow" data-bs-toggle="dropdown">
                                <i className="bx bx-dots-vertical-rounded"></i>
                              </button>
                              <div className="dropdown-menu">
                                <Link className="dropdown-item" to={`/facility/storingen/${storing._id}`}>
                                  <i className="bx bx-show me-1"></i> Bekijk
                                </Link>
                                <Link className="dropdown-item" to={`/facility/storingen/${storing._id}/edit`}>
                                  <i className="bx bx-edit me-1"></i> Bewerk
                                </Link>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="d-flex flex-column justify-content-center align-items-center py-5">
                  <div className="avatar avatar-xl mb-3">
                    <i className="bx bx-inbox bx-lg text-muted"></i>
                  </div>
                  <h6 className="text-muted">Geen recente storingen gevonden</h6>
                  <p className="text-muted mb-0">Er zijn nog geen storingen geregistreerd</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  storingenDashboard: state.facilityManagement?.storingenDashboard,
  storingen: state.facilityManagement?.storingen?.results || []
});

const mapDispatchToProps = {
  fetchStoringenDashboard,
  fetchStoringen,
  message
};

export default connect(mapStateToProps, mapDispatchToProps)(FacilityManagementDashboard);